// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { DocumentScope } from '@shared/data-access/types/engine-types';
import type { TFunction } from 'i18next';
import { z } from 'zod';
import type { DocumentCategory } from '../hooks/use-document-catalog';

const optionalTrimmedString = z.string().trim();

const uploadDocumentFormFields = z.object({
  scope: z.nativeEnum(DocumentScope),
  category: z.string().trim().min(1),
  subcategory: optionalTrimmedString,
  title: z.string().trim().min(1),
  issuer: optionalTrimmedString,
  amount: optionalTrimmedString,
  currency: optionalTrimmedString,
  document_date: optionalTrimmedString,
  expiry_date: optionalTrimmedString,
  language: optionalTrimmedString,
  description: optionalTrimmedString,
});

export type UploadDocumentFormData = z.infer<typeof uploadDocumentFormFields>;

export const createUploadDocumentFormSchema = (t: TFunction) =>
  uploadDocumentFormFields.refine(
    data => {
      const subcategories =
        (
          t(`documents.catalog.scopes.${data.scope}.categories`, {
            ns: 'components',
            returnObjects: true,
          }) as DocumentCategory[]
        ).find(({ id }) => id === data.category)?.subcategories ?? [];

      return subcategories.length === 0
        ? data.subcategory === ''
        : subcategories.includes(data.subcategory);
    },
    {
      message: 'Invalid subcategory for selected category',
      path: ['subcategory'],
    },
  );

export const defaultUploadDocumentFormValues = (): UploadDocumentFormData => ({
  scope: DocumentScope.Personal,
  category: '',
  subcategory: '',
  title: '',
  issuer: '',
  amount: '',
  currency: 'eur',
  document_date: '',
  expiry_date: '',
  language: '',
  description: '',
});
